
import { useState, useEffect } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { extractAndValidateTenantId, determineTenantType } from "@/utils/authUtils";
import { checkUserAccess } from "@/services/authService";

export type TenantType = "admin" | "tenant";

export interface AuthStatus {
  isRegistered: boolean;
  isAuthorized: boolean;
  isChecking: boolean;
}

export function useAuthState() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantType, setTenantType] = useState<TenantType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<AuthStatus>({
    isRegistered: false,
    isAuthorized: false,
    isChecking: true
  });
  const [lastAccessCheck, setLastAccessCheck] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();

  // Verify user access rights with caching
  const verifyUserAccess = async (user: User, forceCheck = false) => {
    if (!user) return;
    
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    
    // Use cache if available and not forced (skip check during token refresh)
    if (!forceCheck && lastAccessCheck && (now - lastAccessCheck < CACHE_DURATION)) {
      console.log("Using cached access check, skipping verification");
      return;
    }
    
    try {
      // Only show loading spinner during initial load
      if (isInitialLoad) {
        setAuthStatus(prev => ({ ...prev, isChecking: true }));
      }
      
      // Check if the user is registered as a master user
      const hasAccess = await checkUserAccess(user.id);
      setAuthStatus(prev => ({ 
        ...prev, 
        isRegistered: hasAccess, 
        isAuthorized: hasAccess,
        isChecking: false
      }));
      
      // Update cache timestamp
      setLastAccessCheck(now);
      setIsInitialLoad(false);
      
      // If not registered, redirect to appropriate page
      if (!hasAccess) {
        // Check current location to avoid redirect loops
        const currentPath = window.location.pathname;
        if (
          currentPath !== '/signup' && 
          currentPath !== '/ms-signup' && 
          currentPath !== '/signup/complete' && 
          currentPath !== '/error' &&
          !currentPath.startsWith('/login')
        ) {
          navigate('/error');
        }
      }
    } catch (error) {
      console.error("Error verifying user access:", error);
      setAuthStatus(prev => ({ ...prev, isChecking: false }));
      setIsInitialLoad(false);
    }
  };

  // Process user data on authentication
  const processUserData = (currentUser: User | null) => {
    if (!currentUser) {
      setTenantId(null);
      setTenantType(null);
      setAuthStatus({
        isRegistered: false,
        isAuthorized: false,
        isChecking: false
      });
      return;
    }
    
    try {
      // Extract and validate tenant ID
      const extractedTenantId = extractAndValidateTenantId(currentUser);
      setTenantId(extractedTenantId);
      
      // Determine tenant type
      const userTenantType = determineTenantType(currentUser);
      setTenantType(userTenantType);
      
      // Verify access
      verifyUserAccess(currentUser);
    } catch (error: any) {
      console.error("Auth error:", error.message);
      toast.error(error.message);
      // Sign out if there's an authentication error
      supabase.auth.signOut().then(() => {
        navigate("/login");
      });
    }
  };

  // Initialize auth state
  useEffect(() => {
    console.log("Setting up auth listeners");
    
    // Set up the auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state change detected:", event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          processUserData(currentSession.user);
        } else {
          processUserData(null);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log("Initial session check:", !!initialSession);
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      
      if (initialSession?.user) {
        processUserData(initialSession.user);
      } else {
        processUserData(null);
      }
      
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Allow manually refreshing auth status (force check)
  const refreshAuthStatus = async () => {
    if (user) {
      await verifyUserAccess(user, true);
    }
  };

  return {
    session,
    user,
    tenantId,
    tenantType,
    isLoading,
    authStatus,
    refreshAuthStatus,
    isInitialLoad
  };
}
